import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox,
    IconButton,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { CalendarToday as CalendarTodayIcon } from "@mui/icons-material";
import InitialTradeForm from "./InitialTradeForm";
import SelectionStep from "./SelectionForm";
import SuccessStep from "./SuccessForm";
import { RootState, useAppDispatch } from "../../../redux/store";
import { setFilterEndDate, setFilterStartDate, setSwapNote } from "../../../redux/features/schedule/ScheduleSlice";
import { useSelector } from "react-redux";
import { SwappableEmployee } from "../../../types/state/swap";
import {
    createSwapRequest,
    createSwapRequestV2,
    getSwapEmployees,
    getSwapEmployeesV2,
} from "../../../redux/features/schedule/ScheduleApi";
import { CreateSwapRequest, ShiftType } from "../../../types";
import { IUser } from "../../../redux/features/user/models/user";
import { scheduler } from "timers/promises";
import { toast } from "react-toastify";

const TradeShift = () => {
    const dispatch = useAppDispatch();
    // Separate state for start and end dates
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const fmShift = useSelector((state: RootState) => state.schedule.selectedShift) as ShiftType;
    const selectedEmployees = useSelector((state: RootState) => state.schedule.selectedSwapEmployees);
    const [note, setNote] = useState("");
    const [step, setStep] = useState(1);
    const filterStartDate = useSelector((state: RootState) => state.schedule.swapFilterStartDate);
    const filterEndDate = useSelector((state: RootState) => state.schedule.swapFilterEndDate);
    const getEmployeeFetchStatus = useSelector((state: RootState) => state.schedule.requestState);
    const [oldSearchContents, setOldSearchContents] = useState("");
    const User = useSelector((state: any) => state.user.user) as IUser;
    const actionConsecutiveDays = useSelector((state: RootState) => state.schedule.actionConsecutiveDays);

    const handleContinue = () => {
        dispatch(setSwapNote(note));
        setStep(2);
    };

    useEffect(() => {
        setStartDate(filterStartDate ? new Date(filterStartDate) : null);
        setEndDate(filterEndDate ? new Date(filterEndDate) : null);
    }, [filterStartDate, filterEndDate]);

    const handleNameSearchChange = (value: string) => {
        setOldSearchContents(value);
        const payload = {
            request_type: 1,
            start_date: startDate ? startDate.toISOString() : new Date().toISOString(),
            end_date: endDate ? endDate.toISOString() : new Date(new Date().getFullYear(), 11, 31).toISOString(),
            assigned_shift_code: fmShift.osShiftCode || "",
            department_code: (User as any).department_code || "",
            assigned_department_code: (User as any).department_code || "",
            group_code: [],
            name_search: "",
            days_in_swapping_block: actionConsecutiveDays,
            schedule_id: `${fmShift.id}`,
        };

        if (value.length > 3 && getEmployeeFetchStatus === "FULFILLED" && value !== oldSearchContents) {
            payload.name_search = value;
        }

        if (value.length === 0 || value.length > 3) {
            dispatch(getSwapEmployeesV2(payload));
        }

        // dispatch(getSwapEmployees(payload));
    };

    const handleSuccess = () => {
    
            const empMap: { [key: string]: string[] } = selectedEmployees.reduce((map, employee) => {
                if (employee.emp_id !== undefined && employee.emp_id !== null) {
                    if (!map[employee.emp_id]) {
                        map[employee.emp_id!.toString()] = [];
                    }
                    map[employee.emp_id!.toString()].push(employee.schedule_id!.toString());
                }
                return map;
            }, {} as Record<string, string[]>);
    
            const payload: CreateSwapRequest = {
                schedule_id: fmShift.id!,
                emp_schedule_map: empMap,
                days_in_swapping_block: actionConsecutiveDays,
                notes: note,
            };
    
            // Return early if emp_schedule_map is empty
            if (Object.keys(payload.emp_schedule_map).length === 0) {
                toast.error("Please select at least one employee to swap.");
                return;
            }
    
            // dispatch(createSwapRequest(payload));
            dispatch(createSwapRequestV2(payload));
            setStep(3);
        };

    const handleStartDateChange = (date: Date | null) => {
        if (date) {
            dispatch(setFilterStartDate(date.toISOString()));
        }
        setStartDate(date);
    };

    const handleEndDateChange = (date: Date | null) => {
        if (date) {
            dispatch(setFilterEndDate(date.toISOString()));
        }
        setEndDate(date);
    };

    const handleSearch = () => {
        // If no start date, default to January 1st of the current year
        const defaultStartDate = new Date(new Date().getFullYear(), 0, 1); // January 1st of the current year

        // If no end date, default to December 31st of the current year
        const defaultEndDate = new Date(new Date().getFullYear(), 11, 31); // December 31st of the current year
        const payload = {
            request_type: 1,
            start_date: (startDate ? startDate : defaultStartDate).toISOString(),
            end_date: (endDate ? endDate : defaultEndDate).toISOString(),
            assigned_shift_code: fmShift.osShiftCode || "",
            department_code: (User as any).department_code || "",
            assigned_department_code: (User as any).department_code || "",
            group_code: [],
            name_search: "",
            days_in_swapping_block: actionConsecutiveDays,
            schedule_id: `${fmShift.id}`,
        };

        if (startDate && endDate) {
            // dispatch(getSwapEmployees(payload));
            dispatch(getSwapEmployeesV2(payload));
        }
    };

    return (
        <Box padding={2}>
            {step === 1 && <InitialTradeForm onContinue={handleContinue} setNote={setNote} />}
            {step === 2 && (
                <SelectionStep
                    startDate={startDate}
                    endDate={endDate}
                    handleStartDateChange={handleStartDateChange}
                    handleEndDateChange={handleEndDateChange}
                    onSuccess={handleSuccess}
                    handleNameSearch={handleNameSearchChange}
                    onSearch={handleSearch}
                />
            )}
            {step === 3 && <SuccessStep />}
        </Box>
    );
};

export default TradeShift;
